import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Box, Paper, Button, Alert,
    CircularProgress, Stack, Chip, Divider, List, ListItem, ListItemIcon, ListItemText,
    TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useTheme, useMediaQuery
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';

import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { filledInputSx } from './FormDialog';

// Memoized card for mobile view
const SecurityCard = React.memo(({ sec }) => {
    const { t } = useTranslation();
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label={sec.symbol} size="small" color="primary" variant="outlined" sx={{ fontWeight: 800 }} />
                        <Chip label={sec.security_type} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {sec.name}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    {t('Currency:')} <strong>{sec.currency}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {t('Exchange:')} <strong>{sec.exchange || '-'}</strong>
                </Typography>
            </Box>
        </Paper>
    );
});

const AdminSymbolSync = () => {
    const { t } = useTranslation();
    const { api } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [syncReport, setSyncReport] = useState(null);
    const [error, setError] = useState(null);

    // Securities Search & Edit State
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    const fetchStats = async () => {
        try {
            const response = await api.get('/admin/securities/stats');
            setStats(response.data);
        } catch (err) {
            console.error("Failed to fetch securities stats", err);
        }
    };

    const handleSearch = useCallback(async (queryStr = '') => {
        setSearchLoading(true);
        try {
            const qVal = (queryStr || '').trim();
            const response = await api.get('/investments/securities/search', { params: { q: qVal, limit: 30 } });
            setSearchResults(response.data);
        } catch (err) {
            console.error("Failed to search securities", err);
        } finally {
            setSearchLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchStats();
        handleSearch('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSync = async () => {
        setLoading(true);
        setError(null);
        setSyncReport(null);
        try {
            const response = await api.post('/admin/securities/sync');
            setSyncReport(response.data);
            fetchStats();
            handleSearch(searchQuery);
        } catch (err) {
            console.error("Symbol sync failed", err);
            setError(err.response?.data?.detail || t("Failed to sync securities symbols from stock exchange feeds."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
                    {t('Stock Exchange Symbol Sync & Securities Management')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('Populate, update, and manage US & Canadian equities and ETFs in local database.')}
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={3}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StorageIcon color="primary" /> {t('Symbol Repository Stats')}
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Chip
                                label={`${t('Total Symbols:')} ${stats?.total_symbols || 0}`}
                                color="primary"
                                sx={{ fontWeight: 700 }}
                            />
                            <Chip
                                label={`${t('Last Loaded:')} ${stats?.last_loaded_at ? new Date(stats.last_loaded_at).toLocaleString() : t('Never')}`}
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                        onClick={handleSync}
                        disabled={loading}
                        sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 700 }}
                    >
                        {loading ? t('Syncing Feeds...') : t('Sync Stock Exchange Symbols')}
                    </Button>
                </Stack>

                {syncReport && (
                    <Box sx={{ mt: 4 }}>
                        <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ borderRadius: 2, mb: 3 }}>
                            {t('Symbol synchronization completed successfully!')}
                        </Alert>

                        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                                {t('SYNC SUMMARY REPORT')}
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 2 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">{t('NEW SYMBOLS ADDED')}</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'success.main' }}>+{syncReport.added_count}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">{t('UPDATED SYMBOLS')}</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'info.main' }}>{syncReport.updated_count}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">{t('TOTAL IN DATABASE')}</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800 }}>{syncReport.total_symbols}</Typography>
                                </Box>
                            </Stack>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                                {t('DATA SOURCES UTILIZED')}
                            </Typography>
                            <List size="small" disablePadding>
                                {syncReport.sources.map((src, idx) => (
                                    <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 28, color: 'primary.main' }}>
                                            <PublicIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={src} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Box>
                )}
            </Paper>

            {/* Manage Individual Securities Section */}
            <Paper sx={{ p: 4, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIcon color="primary" /> {t('Manage & Edit Securities Repository')}
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        placeholder={t('Search securities by symbol or company name (e.g. AAPL, SHOP)...')}
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            handleSearch(e.target.value);
                        }}
                        variant="filled"
                        InputProps={{ disableUnderline: true }}
                        sx={filledInputSx}
                    />
                </Box>

                {searchLoading ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : searchResults.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography color="text.secondary">{t('No securities matched search query.')}</Typography>
                    </Paper>
                ) : isMobile ? (
                    <Box sx={{ mb: 2 }}>
                        {searchResults.map((sec) => (
                            <SecurityCard
                                key={sec.id}
                                sec={sec}
                            />
                        ))}
                    </Box>
                ) : (
                    <TableContainer variant="outlined" sx={{ width: '100%', overflowX: 'auto', borderRadius: 2 }}>
                        <Table size="small" sx={{ minWidth: 600 }}>
                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Symbol')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Security Name')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Type')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Currency')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Exchange')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {searchResults.map((sec) => (
                                    <TableRow key={sec.id} hover>
                                        <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                            <Chip label={sec.symbol} size="small" color="primary" variant="outlined" />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{sec.name}</TableCell>
                                        <TableCell>{sec.security_type}</TableCell>
                                        <TableCell>{sec.currency}</TableCell>
                                        <TableCell>{sec.exchange || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Container>
    );
};

export default AdminSymbolSync;
